import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronsUpDown, X, Loader2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../ui/command";
import { Badge } from "../ui/badge";
import { cn } from "../../lib/utils";

/**
 * Reusable searchable select — single or multi, static options or async server-side search.
 * props:
 *  - value: string | string[]
 *  - onChange: (v) => void
 *  - options: [{ value, label, sub? }]         (static, locally filtered)
 *  - loadOptions: async (query) => [{value,label,sub}]  (server-side; overrides local filter)
 *  - multiple, placeholder, disabled, error (bool), minChars (default 1), testid, className
 */
export function SearchableSelect({ value, onChange, options = [], loadOptions, multiple = false,
  placeholder = "Select…", disabled = false, error = false, minChars = 1, testid = "searchable-select", className }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [remote, setRemote] = useState([]);
  const [loading, setLoading] = useState(false);
  const labelCache = useRef({});
  const debRef = useRef();
  const seqRef = useRef(0);

  const isMulti = multiple;
  const selected = isMulti ? (Array.isArray(value) ? value : []) : (value ? [value] : []);

  useEffect(() => { options.forEach((o) => { labelCache.current[o.value] = o.label; }); }, [options]);

  useEffect(() => {
    if (!loadOptions || !open) return;
    if (query.length < minChars && query.length !== 0) return;
    clearTimeout(debRef.current);
    const controller = new AbortController();
    debRef.current = setTimeout(async () => {
      const reqId = ++seqRef.current;          // latest-request-wins guard
      setLoading(true);
      try {
        const res = await loadOptions(query, controller.signal);
        if (reqId !== seqRef.current) return;   // a newer keystroke superseded this one
        (res || []).forEach((o) => { labelCache.current[o.value] = o.label; });
        setRemote(res || []);
      } catch { if (reqId === seqRef.current) setRemote([]); }
      finally { if (reqId === seqRef.current) setLoading(false); }
    }, 250);
    return () => { clearTimeout(debRef.current); controller.abort(); };  // cancel stale in-flight request
  }, [query, open, loadOptions, minChars]);

  const list = loadOptions ? remote : options;
  // Resolve from current options/remote synchronously so the label shows on the
  // very first render (the ref cache alone doesn't survive a remount / re-render).
  const labelOf = (v) => {
    const o = options.find((x) => x.value === v) || remote.find((x) => x.value === v);
    return o ? o.label : (labelCache.current[v] ?? v);
  };

  const toggle = (v) => {
    if (isMulti) {
      const next = selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v];
      onChange(next);
    } else {
      onChange(v === value ? value : v);
      setOpen(false);
    }
  };
  const clear = (e) => { e.stopPropagation(); onChange(isMulti ? [] : ""); };

  return (
    <Popover open={open} onOpenChange={(o) => { if (!disabled) setOpen(o); }}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testid} disabled={disabled}
          className={cn("flex min-h-9 w-full items-center justify-between gap-2 rounded-md border bg-white px-3 py-1.5 text-left text-sm",
            error ? "border-red-400" : "border-slate-200", disabled && "cursor-not-allowed opacity-60", className)}>
          <span className="flex flex-1 flex-wrap gap-1 overflow-hidden">
            {selected.length === 0 && <span className="text-slate-400">{placeholder}</span>}
            {isMulti
              ? selected.map((v) => <Badge key={v} variant="secondary" className="gap-1 text-xs">{labelOf(v)}<X className="h-3 w-3 cursor-pointer" onClick={(e) => { e.stopPropagation(); toggle(v); }} /></Badge>)
              : selected.length > 0 && <span className="truncate text-slate-800">{labelOf(selected[0])}</span>}
          </span>
          <span className="flex items-center gap-1">
            {selected.length > 0 && !disabled && <X className="h-3.5 w-3.5 text-slate-400 hover:text-slate-700" onClick={clear} data-testid={`${testid}-clear`} />}
            <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-slate-400" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] min-w-[220px] p-0" align="start">
        <Command shouldFilter={!loadOptions}>
          <CommandInput placeholder="Search…" value={query} onValueChange={setQuery} data-testid={`${testid}-input`} />
          <CommandList>
            {loading && <div className="flex items-center gap-2 px-3 py-4 text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Searching…</div>}
            {!loading && <CommandEmpty>No results</CommandEmpty>}
            {!loading && (
              <CommandGroup>
                {list.map((o) => {
                  const on = selected.includes(o.value);
                  return (
                    <CommandItem key={o.value} value={`${o.label} ${o.value}`} disabled={o.disabled} onSelect={() => { if (!o.disabled) toggle(o.value); }} data-testid={`${testid}-opt-${o.value}`} className={o.disabled ? "cursor-not-allowed opacity-80" : "cursor-pointer"}>
                      <Check className={cn("mr-2 h-4 w-4", on ? "opacity-100 text-blue-600" : "opacity-0")} />
                      <div className="flex flex-col"><span>{o.label}</span>{o.sub && <span className="text-xs text-slate-400">{o.sub}</span>}</div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
